import { useEffect, useState, useCallback } from "react";
import { Minus, Plus, Trash2, ShoppingCart, Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/common";
import { CashActions } from "@/components/cash/CashActions";
import { OpenCashDialog } from "@/components/cash/OpenCashDialog";
import { ProductPicker } from "@/components/ProductPicker";
import { PaymentDialog, ReceiptDialog } from "@/components/PaymentDialog";
import { SellBlocked } from "@/components/TrialBanner";
import { RecentSales } from "@/components/RecentSales";
import { OptionsDialog } from "@/components/OptionsDialog";
import { NfceDialog } from "@/components/NfceDialog";
import { useDocumentos } from "@/fiscal";
import { DanfeDialog } from "@/components/DanfeDialog";
import { useAuth } from "@/context/AuthContext";
import { api, brl, fmtDateTime, canDo, canSee, isManager } from "@/lib/api";
import { directPrint } from "@/lib/qz";
import { saleReceipt } from "@/lib/receipts";
import { useWallet } from "@/lib/loyalty";
import { toast } from "sonner";

function cashText(cash, manager) {
  if (!cash?.open) return manager ? "Caixa fechado. Abra o caixa para liberar as vendas." : "Caixa fechado. Somente o administrador ou gerente pode abrir o caixa.";
  if (cash.allowed === false) return "Há um caixa aberto, mas seu usuário não foi liberado nele. Fale com o administrador ou gerente.";
  return `Caixa #${cash.session.number} aberto por ${cash.session.opened_by} em ${fmtDateTime(cash.session.opened_at)} · Dinheiro esperado ${brl(cash.summary.expected_cash)}`;
}

export function CashBar({ cash, reload, cashMode = "pdv" }) {
  const { session } = useAuth();
  const manager = isManager(session.user);
  const [opening, setOpening] = useState(false);
  const open = !!cash?.open;
  return (
    <div className="card-surface flex flex-wrap items-center gap-3 px-4 py-3" data-testid="cash-bar">
      {open ? <Unlock className="h-4 w-4 text-emerald-500" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
      <span className={`text-sm flex-1 ${open ? "" : "text-muted-foreground"}`} data-testid="cash-status">{cashText(cash, manager)}</span>
      {manager && open && <CashActions item={cash} onChanged={reload} />}
      {manager && !open && <Button size="sm" onClick={() => setOpening(true)} data-testid="cash-open-btn">Abrir caixa</Button>}
      {opening && <OpenCashDialog mode={cashMode} defaultUserId={session.user.id} onClose={() => setOpening(false)} onDone={() => { setOpening(false); reload(); }} />}
    </div>
  );
}

function Cart({ cart, setQty, setNotes, setPrice, canPrice, notes, setSaleNotes, customers, customerId, setCustomerId, onPay }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const count = cart.reduce((s, i) => s + (Number.isInteger(i.qty) ? i.qty : 1), 0);
  return (
    <div className="card-surface flex flex-col lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)]" id="pos-cart" data-testid="pos-cart">
      <div className="flex items-center justify-between border-b p-4">
        <p className="font-display font-semibold flex items-center gap-2"><ShoppingCart className="h-4 w-4" />Carrinho</p>
        <span className="text-xs text-muted-foreground" data-testid="pos-cart-count">{count} itens</span>
      </div>
      <ul className="flex-1 overflow-y-auto divide-y" data-testid="pos-cart-item-list">
        {cart.map((i) => (
          <li key={i.key} className="flex items-center gap-2 p-3" data-testid={`cart-item-${i.id}`}>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{i.name}</p>
              <p className="font-mono text-xs text-muted-foreground">
                {canPrice ? <input type="number" step="0.01" value={i.price} onChange={(e) => setPrice(i.key, e.target.value)} className="w-20 bg-transparent font-mono outline-none border-b border-dashed" data-testid={`cart-price-${i.id}`} /> : brl(i.price)} · {brl(i.price * i.qty)}
              </p>
              <input value={i.notes || ""} onChange={(e) => setNotes(i.key, e.target.value)} placeholder="+ observação"
                className="mt-1 w-full bg-transparent text-xs text-amber-600 dark:text-amber-400 placeholder:text-muted-foreground/60 outline-none" data-testid={`cart-notes-${i.id}`} />
            </div>
            <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQty(i.key, i.qty - 1)} data-testid={`cart-dec-${i.id}`}>{i.qty === 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}</Button>
            <input type="number" step="any" value={i.qty} onChange={(e) => setQty(i.key, Number(e.target.value) > 0 ? Number(e.target.value) : 1)} className="w-14 bg-transparent text-center font-mono text-sm outline-none" data-testid={`cart-qty-${i.id}`} />
            <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => setQty(i.key, i.qty + 1)} data-testid={`cart-inc-${i.id}`}><Plus className="h-4 w-4" /></Button>
          </li>
        ))}
        {!cart.length && <li className="p-8 text-center text-sm text-muted-foreground">Toque nos produtos para adicionar.</li>}
      </ul>
      <div className="border-t p-4 space-y-3">
        <Select value={customerId || "none"} onValueChange={(v) => setCustomerId(v === "none" ? null : v)}>
          <SelectTrigger data-testid="pos-customer-select"><SelectValue placeholder="Cliente (opcional)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Consumidor final</SelectItem>
            {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={notes} onChange={(e) => setSaleNotes(e.target.value)} placeholder="Observações da venda" data-testid="pos-sale-notes-input" />
        <div className="flex items-end justify-between">
          <span className="text-sm text-muted-foreground">Total</span>
          <span className="font-display text-3xl font-bold" data-testid="pos-cart-total">{brl(total)}</span>
        </div>
        <Button className="w-full h-14 text-lg" disabled={!cart.length} onClick={onPay} data-testid="pos-pay-button">Cobrar {brl(total)}</Button>
      </div>
    </div>
  );
}

export default function POS() {
  const [cart, setCart] = useState([]);
  const [cash, setCash] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [paying, setPaying] = useState(false);
  const [sale, setSale] = useState(null);
  const [notes, setSaleNotes] = useState("");
  const [picking, setPicking] = useState(null);
  const [nfceSale, setNfceSale] = useState(null);
  const [danfe, setDanfe] = useState(null);
  const { session } = useAuth();
  const docsFiscais = useDocumentos();
  // NFC-e só aparece se a loja tem o Fiscal E o Admin Master liberou a NFC-e (credenciamento na SEFAZ)
  const nfceOn = canSee(session.user, "pdv") && (session.modules == null || session.modules.includes("fiscal")) && docsFiscais?.nfce?.liberado === true;
  const st = session.tenant.settings;
  const canPrice = canDo(session.user, "change_price");

  const loadCash = useCallback(() => api.get("/cash/current").then((r) => setCash(r.data)), []);
  const loadCustomers = useCallback(() => api.get("/customers").then((r) => setCustomers(r.data.filter((c) => c.active !== false))), []);
  useEffect(() => {
    loadCash();
    loadCustomers();
  }, [loadCash, loadCustomers]);

  const addLine = ({ product: p, options = [], qty = 1, notes: n = "", name, price }) => setCart((c) => {
    const key = `${p.id}|${JSON.stringify(options)}|${n}`;
    const ex = c.find((i) => i.key === key);
    return ex ? c.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i)) : [...c, { key, id: p.id, name: name || p.name, price: price ?? p.price, basePrice: price ?? p.price, qty, notes: n, options }];
  });
  const add = (p, qty) => (p.option_groups?.length ? setPicking(p) : addLine({ product: p, qty }));
  const setQty = (key, qty) => setCart((c) => (qty <= 0 ? c.filter((i) => i.key !== key) : c.map((i) => (i.key === key ? { ...i, qty } : i))));
  const setNotes = (key, n) => setCart((c) => c.map((i) => (i.key === key ? { ...i, notes: n } : i)));
  const setPrice = (key, v) => setCart((c) => c.map((i) => (i.key === key ? { ...i, price: v === "" ? "" : Number(v) } : i)));
  const reopen = (s) => {
    setCart(s.items.filter((i) => i.product_id).map((i) => ({ key: i.id, id: i.product_id, name: i.name, price: i.price, basePrice: i.price, qty: i.qty, notes: i.notes || "", options: i.options || [] })));
    setCustomerId(s.customer_id || null);
    setSaleNotes(s.notes || "");
    loadCash();
  };
  const subtotal = cart.reduce((s, i) => s + (Number(i.price) || 0) * i.qty, 0);
  const customer = customers.find((c) => c.id === customerId);
  const wallet = useWallet(customer?.id);
  const loyalty = wallet?.enabled ? wallet : null;

  const pay = async ({ payments, discount, surcharge, apply_service_fee, loyalty_discount, loyalty_rewards, emit_nfce }) => {
    const items = cart.map((i) => ({ product_id: i.id, qty: i.qty, notes: i.notes || "", options: i.options || [], ...(Number(i.price) !== i.basePrice ? { unit_price: Number(i.price) || 0 } : {}) }));
    const { data } = await api.post("/sales", { items, payments, discount, surcharge, apply_service_fee, notes, customer_id: customerId, loyalty_discount: loyalty_discount || 0, loyalty_rewards: loyalty_rewards || [] });
    (data.stock_alerts || []).forEach((a) => toast.warning(a));
    setSaleNotes("");
    emit_nfce ? setNfceSale(data) : setSale(data);
    directPrint(session, "pdv", (pr) => saleReceipt(data, session.tenant, pr));
    setCart([]);
    setCustomerId(null);
    loadCash();
    loadCustomers();
  };

  return (
    <>
      <PageHeader eyebrow="Venda rápida · balcão" title="PDV / Caixa" subtitle={`Operador: ${session.user.name}`} actions={<RecentSales onReopen={reopen} />} />
      <SellBlocked />
      <CashBar cash={cash} reload={loadCash} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8"><ProductPicker onPick={add} quickCreate /></div>
        <div className="lg:col-span-4"><Cart cart={cart} setQty={setQty} setNotes={setNotes} setPrice={setPrice} canPrice={canPrice} notes={notes} setSaleNotes={setSaleNotes} customers={customers} customerId={customerId} setCustomerId={setCustomerId} onPay={() => setPaying(true)} /></div>
      </div>
      {cart.length > 0 && (
        <button onClick={() => document.getElementById("pos-cart")?.scrollIntoView({ behavior: "smooth" })}
          className="lg:hidden fixed bottom-20 left-4 right-4 z-30 rounded-xl bg-primary px-4 py-3 text-primary-foreground font-semibold shadow-lg flex justify-between" data-testid="pos-mobile-cart-btn">
          <span>Ver carrinho ({cart.reduce((s, i) => s + (Number.isInteger(i.qty) ? i.qty : 1), 0)})</span><span className="font-mono">{brl(subtotal)}</span>
        </button>
      )}
      <PaymentDialog open={paying} onOpenChange={setPaying} subtotal={subtotal} onConfirm={pay} title="Finalizar venda" allowSurcharge serviceFeePct={st.service_fee_pct} allowDiscount={canDo(session.user, "apply_discount")} loyalty={loyalty} nfce={nfceOn} />
      <OptionsDialog product={picking} onClose={() => setPicking(null)} onAdd={addLine} />
      <ReceiptDialog sale={sale} onClose={() => setSale(null)} />
      <NfceDialog saleId={nfceSale?.id} onDone={(nota) => { const s = nfceSale; setNfceSale(null); if (nota?.status === "autorizada") setDanfe(nota); else setSale(s); }} />
      <DanfeDialog nota={danfe} onClose={() => setDanfe(null)} closeLabel="Nova venda" />
    </>
  );
}
